/**
 * D252: the ORDER interceptor — a clause said to a body is the body's to
 * answer, before any other reading of it.
 *
 * @remarks
 * The parser reads `tell TALLY to follow me`, `TALLY, follow me` and
 * `TALLY follow me` as the clause `follow me` addressed to TALLY
 * (`read-order.ts`); the addressee rides on the command. Every other
 * interceptor reads a command as the player's own act — `follow me` alone is
 * a verb aimed at the player's hands — so this one runs first and claims the
 * whole command. An order is speech, so it is judged as speech is: a body
 * the player can address is one they can tell, heard through steel or not,
 * and no reach is asked of it. What the body does is its `orders` table's.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { commandInHandOf } from '../conditions/command-in-hand.js';
import { actorIdForEntityRef } from '../entities/entity-ref.js';
import { narrowed } from './narrow-parsed-command.js';

/**
 * Narrow a command said to somebody into the order they were given.
 *
 * @param command - The parsed proposal
 * @param _bundle - The loaded bundle (the interceptor signature; an order needs nothing from it to narrow)
 * @param intentText - The canonical verb the body was told
 * @param _state - The live runtime state (likewise)
 * @returns The order, or `undefined` when the command was said to nobody
 */
export const narrowOrderCommand = (
	command: ParsedCommand,
	_bundle: EngineGameBundle,
	intentText: string,
	_state: RuntimeState | undefined,
): CommandNarrowingOutcome | undefined => {
	const actorId = command.addresseeRef === undefined ? undefined : actorIdForEntityRef(command.addresseeRef);

	if (actorId === undefined) {
		return undefined;
	}

	const { directObjectItemId, indirectObjectItemId, toolItemId } = commandInHandOf(command);

	return narrowed({
		actorId,
		...(directObjectItemId === undefined ? {} : { directObjectItemId }),
		...(indirectObjectItemId === undefined ? {} : { indirectObjectItemId }),
		...(toolItemId === undefined ? {} : { toolItemId }),
		type: 'order',
		verbToken: intentText,
	});
};
